import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import googleIcon from '@/assets/images/google.png';
import appleIcon from '@/assets/images/apple.png';
import { providerLabel, type AuthMethodProvider } from '@/lib/accountLinking';
import SignInMethodsHelpLink from './SignInMethodsHelpLink';

type SocialProvider = 'google' | 'apple';

export interface AuthMethodsSectionProps {
  methods: AuthMethodProvider[];
  /** Cognito sign-in email; falls back to the profile email upstream. */
  signInEmail: string | null;
  isBusy?: boolean;
  onSetPassword: () => void;
  onChangePassword: () => void;
  onConnect: (provider: SocialProvider) => void;
  onRemove: (provider: SocialProvider) => void;
  /** Remove tapped while no password exists; removing would lock the account. */
  onRemoveBlocked: (provider: SocialProvider) => void;
  /** Closes Account Settings (or similar) before opening the sign-in methods FAQ. */
  onSignInMethodsHelpNavigate?: () => void;
}

const SOCIAL_ROWS: SocialProvider[] = ['google', 'apple'];

const rowClassName =
  'flex w-full items-center justify-between gap-3 rounded-xl border border-[#e1e1e7] bg-white px-4 py-3';
const actionClassName =
  'shrink-0 border-none bg-transparent p-0 font-manrope text-sm font-bold text-[#1a1a1c] disabled:opacity-50';
const subtitleClassName = 'font-manrope text-[13px] leading-normal';

function MethodConnectionStatus({ connected }: { connected: boolean }): React.ReactElement {
  if (connected) {
    return (
      <span className={`inline-flex items-center gap-0.5 ${subtitleClassName} text-[#388638]`}>
        <CheckIcon sx={{ fontSize: 14, color: '#388638' }} aria-hidden />
        Connected
      </span>
    );
  }
  return <span className={`${subtitleClassName} text-[#76767f]`}>Not connected</span>;
}

const AuthMethodsSection: React.FC<AuthMethodsSectionProps> = ({
  methods,
  signInEmail,
  isBusy = false,
  onSetPassword,
  onChangePassword,
  onConnect,
  onRemove,
  onRemoveBlocked,
  onSignInMethodsHelpNavigate,
}) => {
  const hasPassword = methods.includes('email');

  return (
    <section className="flex w-full flex-col gap-4" aria-labelledby="sign-in-methods-heading">
      <h3
        id="sign-in-methods-heading"
        className="m-0 font-manrope text-[13px] font-extrabold uppercase tracking-[0.5px] text-[#1a1a1c]"
      >
        Sign-in methods
      </h3>
      <p className="m-0 font-manrope text-sm leading-5 text-[#76767f]">
        Sign in with your email, Google, or Apple.
      </p>

      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        <li className={rowClassName}>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-manrope text-[15px] font-semibold text-[#1a1a1c]">Email</span>
            {hasPassword && signInEmail ? (
              <>
                <span className={`truncate ${subtitleClassName} text-[#76767f]`}>{signInEmail}</span>
                <MethodConnectionStatus connected />
              </>
            ) : (
              <span className={`truncate ${subtitleClassName} text-[#76767f]`}>
                {[signInEmail, 'No password'].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          <button
            type="button"
            className={actionClassName}
            disabled={isBusy}
            onClick={hasPassword ? onChangePassword : onSetPassword}
          >
            {hasPassword ? 'Change password' : 'Set password'}
          </button>
        </li>

        {SOCIAL_ROWS.map((provider) => {
          const isConnected = methods.includes(provider);
          const label = providerLabel(provider);
          return (
            <li key={provider} className={rowClassName}>
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <img
                  src={provider === 'google' ? googleIcon : appleIcon}
                  alt=""
                  className="h-5 w-5 shrink-0 object-contain"
                />
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="font-manrope text-[15px] font-semibold text-[#1a1a1c]">{label}</span>
                  <MethodConnectionStatus connected={isConnected} />
                </div>
              </div>
              {isConnected ? (
                <button
                  type="button"
                  className={`${actionClassName} ${hasPassword ? '' : 'text-[#76767f]'}`}
                  disabled={isBusy}
                  aria-disabled={!hasPassword}
                  aria-label={`Remove ${label}`}
                  onClick={() => (hasPassword ? onRemove(provider) : onRemoveBlocked(provider))}
                >
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  className={actionClassName}
                  disabled={isBusy}
                  aria-label={`Connect ${label}`}
                  onClick={() => onConnect(provider)}
                >
                  Connect
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <p className="m-0 font-manrope text-[13px] text-[#76767f]">Keep at least one sign-in method.</p>
      {onSignInMethodsHelpNavigate && (
        <SignInMethodsHelpLink
          onBeforeNavigate={onSignInMethodsHelpNavigate}
          className="w-fit font-manrope text-[13px] font-bold text-[#1a1a1c] underline decoration-[#1a1a1c]/30 underline-offset-2"
        />
      )}
    </section>
  );
};

export default AuthMethodsSection;
